import { useEffect, useState } from 'react';

import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Descriptions, Popconfirm, Tooltip, Upload, notification } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';

import programConfig from '@/config/programs/formFields';
import useDeleteEOB from '@/hooks/useDeleteEOB';
import useDeleteHICF from '@/hooks/useDeleteHICF';
import { useFieldRenderer } from '@/hooks/useFieldRenderer';
import useGlobalConfig from '@/store/global';
import { AssistanceRequestStatus } from '@/types/AssistanceRequest';
import { ProgramName } from '@/types/global';

interface RequestDetailsCardProps {
  data: any;
  program: ProgramName;
  isOctaPharma: boolean;
}

const RequestDetailsCard: React.FC<RequestDetailsCardProps> = ({ data, program, isOctaPharma }) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [hicfFileList, setHicfFileList] = useState<any[]>([]);
  const { mutateAsync: deleteEOB } = useDeleteEOB({ id: data.id });
  const { mutateAsync: deleteHICF } = useDeleteHICF({ id: data.id });
  const queryClient = useQueryClient();
  const [toast, contextHolder] = notification.useNotification();
  const changeFormAttachmentIds = (e: any) => {
    setFileList(e.fileList);

    if (e.file.status === 'done') {
      queryClient.invalidateQueries({
        queryKey: ['assistance-request-detail', data.id?.toString()],
      });
    }
  };

  const beforeUpload = (file: any) => {
    const isLt20M = file.size / 1024 / 1024 < 20;
    if (!isLt20M) {
      toast.error({
        message: 'File size limit exceeded',
        description: 'File must be smaller than 20MB!',
      });
      return false;
    }
    return true;
  };

  const changeHicfFormAttachmentIds = (e: any) => {
    setHicfFileList(e.fileList);

    if (e.file.status === 'done') {
      queryClient.invalidateQueries({
        queryKey: ['assistance-request-detail', data.id?.toString()],
      });
    }
  };

  const handleRemoveHICF = async () => {
    try {
      await deleteHICF();
      toast.success({
        message: 'HICF deleted successfully',
        description: 'The HICF has been deleted successfully.',
      });
    } catch (error) {
      toast.error({
        message: 'Failed to delete HICF',
        description: 'The HICF could not be deleted. Please try again.',
      });
    }
  };

  const handleRemoveEOB = async () => {
    try {
      await deleteEOB();
      toast.success({
        message: 'EOB deleted successfully',
        description: 'The EOB has been deleted successfully.',
      });
    } catch (error) {
      toast.error({
        message: 'Failed to delete EOB',
        description: 'The EOB could not be deleted. Please try again.',
      });
    }
  };

  const config = (programConfig as Record<ProgramName, { fields: any[] }>)[program] || {
    fields: [],
  };
  const { renderField } = useFieldRenderer({ data });
  const { meData } = useGlobalConfig();

  const handleDelete = async () => {
    try {
      await axios.delete(`/assistance_requests/${data.id}`);
      window.history.back();
    } catch (error) {
      console.error('Failed to delete assistance request:', error);
    }
  };

  const isFieldVisible = (field: any) => {
    if (!field.hidden) {
      return true;
    }

    if (typeof field.hidden === 'function') {
      return !field.hidden(meData);
    }

    return !field.hidden;
  };

  useEffect(() => {
    setFileList(
      data.eob_url
        ? [
            {
              uid: '1',
              name: data.eob_filename,
              url: data.eob_url,
            },
          ]
        : []
    );

    setHicfFileList(
      data.hicf_url
        ? [
            {
              uid: '2',
              name: data.hicf_filename,
              url: data.hicf_url,
            },
          ]
        : []
    );
  }, [data]);

  return (
    <div className="mx-auto">
      {contextHolder}
      <Card
        title={`REQUEST DETAILS #${data.id} - ${data.patient_first_name} ${data.patient_last_name}`}
        className="w-full"
        extra={
          <div className="flex gap-2">
            <Popconfirm
              title="Are you sure to delete this request?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button danger disabled={data.status === AssistanceRequestStatus.EOB_PAID}>
                Delete Request
              </Button>
            </Popconfirm>
          </div>
        }
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item
            styles={{ label: { width: '60%' } }}
            label={isOctaPharma || program === 'yimmugo' ? 'EOB + HICF (CMS 1500)' : 'EOB'}
          >
            <div className="flex flex-col gap-y-2">
              <Upload
                action={`${axios.defaults.baseURL}/assistance_requests/${data.id}/upload_eob`}
                listType="text"
                fileList={fileList}
                maxCount={1}
                disabled={data.status === 'eob_paid'}
                onRemove={handleRemoveEOB}
                accept="application/pdf, image/*"
                onChange={changeFormAttachmentIds}
                beforeUpload={beforeUpload}
                headers={{
                  Authorization: `${localStorage.getItem('Authorization')}`,
                }}
              >
                <Button className="mr-2 rounded bg-[#5bc0de] text-white" icon={<UploadOutlined />}>
                  {fileList.length > 0 ? 'Replace EOB' : 'Upload EOB'}
                  <Tooltip title="Please upload EOBs as PDF or image files only. Editable formats like Word or text files are not accepted.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Button>
              </Upload>
              {(isOctaPharma || program === 'yimmugo') && (
                <Upload
                  action={`${axios.defaults.baseURL}/assistance_requests/${data.id}/upload_hicf`}
                  listType="text"
                  maxCount={1}
                  disabled={data.status === 'eob_paid'}
                  fileList={hicfFileList}
                  onRemove={handleRemoveHICF}
                  onChange={changeHicfFormAttachmentIds}
                  beforeUpload={beforeUpload}
                  name="hicf_file"
                  headers={{
                    ProgramName: 'wilate',
                    Authorization: `${localStorage.getItem('Authorization')}`,
                  }}
                >
                  <Button
                    className="mr-2 rounded bg-[#5bc0de] text-white"
                    icon={<UploadOutlined />}
                  >
                    {hicfFileList.length > 0 ? 'Replace HICF' : 'Upload HICF'}
                  </Button>
                </Upload>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item styles={{ label: { width: '60%' } }} label="Status">
            {data.status_string}
          </Descriptions.Item>
          {config.fields.filter(isFieldVisible).map((field: any) => (
            <Descriptions.Item key={field.name} label={field.label}>
              {renderField(field)}
            </Descriptions.Item>
          ))}
          <Descriptions.Item label="Created Date">
            {dayjs(data.created_at).format('MM-DD-YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="Updated Date">
            {dayjs(data.updated_at).format('MM-DD-YYYY')}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default RequestDetailsCard;
